/*!
 * FPSMeter 0.3.1 - 9th May 2013
 * https://github.com/Darsain/fpsmeter
 *
 * Licensed under the MIT license.
 * http://opensource.org/licenses/MIT
 */

/**
 * FPSMeter class.
 *
 * @param {Element} anchor  Element to append the meter to. Default is document.body.
 * @param {Object}  options Object with options.
 */
export class FPSMeter {
	constructor(anchor, options) {
		// Optional arguments
		if (this.type(anchor) === 'object' && anchor.nodeType === undefined) {
			options = anchor;
			this.anchor = document.body;
		}
		if (!anchor) {
			this.anchor = document.body;
		}

		this.makeThemes();

		// Private properties
		this.o = this.extend({}, FPSMeter.defaults, options || {});

		this.el = {};
		this.cols = [];
		this.theme, this.heatmaps;
		this.heatDepth = 100;
		this.heating = [];

		this.thisFrameTime = 0;
		this.frameTime = this.o.threshold;
		this.frameStart = 0;
		this.getTime();
		this.lastLoop = this.getTime() - this.frameTime;
		this.time;

		this.fpsHistory = [];
		this.durationHistory = [];

		this.frameID, this.renderID;
		this.showFps = this.o.show === 'fps';
		this.graphHeight, this.count, this.i, this.j;

		// Exposed properties
		this.options = this.o;
		this.fps = 0;
		this.duration = 0;
		this.isPaused = 0;
		
	// Local WindowAnimationTiming interface polyfill
		this.cAF = window.cancelAnimationFrame;
		this.rAF = window.requestAnimationFrame;

		var vendors = ['moz', 'webkit', 'o'];
		var lastTime = 0;

		// For a more accurate WindowAnimationTiming interface implementation, ditch the native
		// requestAnimationFrame when cancelAnimationFrame is not present (older versions of Firefox)
		for (var i = 0, l = vendors.length; i < l && !this.cAF; ++i) {
			this.cAF = window[vendors[i] + 'CancelAnimationFrame'] || window[vendors[i] + 'CancelRequestAnimationFrame'];
			this.rAF = this.cAF && window[vendors[i] + 'RequestAnimationFrame'];
		}

		if (!this.cAF) {
			this.rAF = function (callback) {
				var currTime = this.getTime();
				var timeToCall = Math.max(0, 16 - (currTime - lastTime));
				lastTime = currTime + timeToCall;
				return window.setTimeout(function () { callback(currTime + timeToCall); }, timeToCall);
			};

			this.cAF = function (id) {
				window.clearTimeout(id);
			};
		}

		// Property name for assigning element text content
		this.textProp = this.type(document.createElement('div').textContent) === 'string' ? 'textContent' : 'innerText';

		// Option names that trigger FPSMeter rebuild or reposition when modified
		this.rebuilders = [
			'toggleOn',
			'theme',
			'heat',
			'graph',
			'history'
		];
		this.repositioners = [
			'position',
			'zIndex',
			'left',
			'top',
			'right',
			'bottom',
			'margin'
		];
		// Create meter element
		this.createMeter();
		// Start rendering
		this.requestRender();
	}

	/**
	 * Tick start for measuring the actual rendering duration.
	 *
	 * @return {Void}
	 */
	tickStart() {
		this.frameStart = this.getTime();
	};

	/**
	 * FPS tick.
	 *
	 * @return {Void}
	 */
	tick() {
		this.time = this.getTime();
		this.thisFrameTime = this.time - this.lastLoop;
		this.frameTime += (this.thisFrameTime - this.frameTime) / this.o.smoothing;
		this.fps = 1000 / this.frameTime;
		this.duration = this.frameStart < this.lastLoop ? this.frameTime : this.time - this.frameStart;
		this.lastLoop = this.time;
	};

	/**
	 * Pause display rendering.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	pause() {
		if (this.frameID) {
			this.isPaused = 1;
			window.clearTimeout(this.frameID);
			this.cAF(this.frameID);
			this.cAF(this.renderID);
			this.frameID = this.renderID = 0;
		}
		return this;
	};

	/**
	 * Resume display rendering.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	resume() {
		if (!this.frameID) {
			this.isPaused = 0;
			this.requestRender();
		}
		return this;
	};

	/**
	 * Update options.
	 *
	 * @param {String} name  Option name.
	 * @param {Mixed}  value New value.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	set(name, value) {
		this.o[name] = value;
		this.showFps = this.o.show === 'fps';

		// Rebuild or reposition elements when specific option has been updated
		if (this.inArray(name, this.rebuilders) !== -1) {
			this.createMeter();
		}
		if (this.inArray(name, this.repositioners) !== -1) {
			this.positionMeter();
		}
		return this;
	};

	/**
	 * Change meter into rendering duration mode.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	showDuration() {
		this.set('show', 'ms');
		return this;
	};

	/**
	 * Change meter into FPS mode.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	showFpsFun() {
		this.set('show', 'fps');
		return this;
	};

	/**
	 * Toggles between show: 'fps' and show: 'duration'.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	toggle() {
		this.set('show', this.showFps ? 'ms' : 'fps');
		return this;
	};

	/**
	 * Hide the FPSMeter. Also pauses the rendering.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	hide() {
		this.pause();
		this.el.container.style.display = 'none';
		return this;
	};

	/**
	 * Show the FPSMeter. Also resumes the rendering.
	 *
	 * @return {Object} FPSMeter instance.
	 */
	show() {
		this.resume();
		this.el.container.style.display = 'block';
		return this;
	};

	/**
	 * Check the current FPS and save it in history.
	 *
	 * @return {Void}
	 */
	historyTick() {
		for (this.i = this.o.history; this.i--;) {
			this.fpsHistory[this.i] = this.i === 0 ? this.fps : this.fpsHistory[this.i - 1];
			this.durationHistory[this.i] = this.i === 0 ? this.duration : this.durationHistory[this.i - 1];
		}
	}

	/**
	 * Returns heat hex color based on values passed.
	 *
	 * @param  {Integer} heatmap
	 * @param  {Integer} value
	 * @param  {Integer} min
	 * @param  {Integer} max
	 *
	 * @return {Integer}
	 */
	getHeat(heatmap, value, min, max) {
		return this.heatmaps[0 | heatmap][Math.round(Math.min((value - min) / (max - min) * this.heatDepth, this.heatDepth))];
	}

	/**
	 * Update counter number and legend.
	 *
	 * @return {Void}
	 */
	updateCounter() {
		// Update legend only when changed
		if (this.el.legend.fps !== this.showFps) {
			this.el.legend.fps = this.showFps;
			this.el.legend[this.textProp] = this.showFps ? 'FPS' : 'ms';
		}
		// Update counter with a nicely formated & readable number
		this.count = this.showFps ? this.fps : this.duration;
		this.el.count[this.textProp] = this.count > 999 ? '999+' : this.count.toFixed(this.count > 99 ? 0 : this.o.decimals);
	}

	/**
	 * Render current FPS state.
	 *
	 * @return {Void}
	 */
	render() {
		this.time = this.getTime();
		// If renderer stopped reporting, do a simulated drop to 0 fps
		if (this.lastLoop < this.time - this.o.threshold) {
			this.fps -= this.fps / Math.max(1, this.o.smoothing * 60 / this.o.interval);
			this.duration = 1000 / this.fps;
		}

		this.historyTick();
		this.updateCounter();

		// Apply heat to elements
		if (this.o.heat) {
			if (this.heating.length) {
				for (this.i = this.heating.length; this.i--;) {
					this.heating[this.i].el.style[this.theme[this.heating[this.i].name].heatOn] = this.showFps ?
					this.getHeat(this.theme[this.heating[this.i].name].heatmap, this.fps, 0, this.o.maxFps) :
					this.getHeat(this.theme[this.heating[this.i].name].heatmap, this.duration, this.o.threshold, 0);
				}
			}

			if (this.el.graph && this.theme.column.heatOn) {
				for (this.i = this.cols.length; this.i--;) {
					this.cols[i].style[this.theme.column.heatOn] = this.showFps ?
					this.getHeat(this.theme.column.heatmap, this.fpsHistory[this.i], 0, this.o.maxFps) :
					this.getHeat(this.theme.column.heatmap, this.durationHistory[this.i], this.o.threshold, 0);
				}
			}
		}

		// Update graph columns height
		if (this.el.graph) {
			for (this.j = 0; this.j < this.o.history; this.j++) {
				this.cols[this.j].style.height = (this.showFps ?
					(this.fpsHistory[this.j] ? Math.round(this.graphHeight / this.o.maxFps * Math.min(this.fpsHistory[this.j], this.o.maxFps)) : 0) :
					(this.durationHistory[this.j] ? Math.round(this.graphHeight / this.o.threshold * Math.min(this.durationHistory[this.j], this.o.threshold)) : 0)
				) + 'px';
			}
		}
	}

	/**
	 * Request rendering loop.
	 *
	 * @return {Int} Animation frame index.
	 */
	requestRender() {
		if (this.o.interval < 20) {
			this.frameID = window.requestAnimationFrame(this.requestRender.bind(this));
			this.render();
		} else {
			this.frameID = window.setTimeout(this.requestRender.bind(this), this.o.interval);
			this.renderID = window.requestAnimationFrame(this.render.bind(this));
		}
	}

	/**
	 * Meter events handler.
	 *
	 * @return {Void}
	 */
	eventHandler(event) {
		event = event || window.event;
		if (event.preventDefault) {
			event.preventDefault();
			event.stopPropagation();
		} else {
			event.returnValue = false;
			event.cancelBubble = true;
		}
		this.toggle();
	}

	/**
	 * Destroys the current FPSMeter instance.
	 *
	 * @return {Void}
	 */
	destroy() {
		// Stop rendering
		this.pause();
		// Remove elements
		this.removeMeter();
		// Stop listening
		this.tick = this.tickStart = function () { };
	};

	/**
	 * Remove meter element.
	 *
	 * @return {Void}
	 */
	removeMeter() {
		// Unbind listeners
		if (this.o.toggleOn) {
			this.listener(this.el.container, this.o.toggleOn, this.eventHandler.bind(this), 1);
		}
		// Detach element
		this.anchor.removeChild(this.el.container);
	}

	/**
	 * Sets the theme, and generates heatmaps when needed.
	 */
	setTheme() {
		this.theme = this.themes[this.o.theme];

		// Generate heatmaps
		this.heatmaps = this.theme.compiledHeatmaps || [];
		if (!this.heatmaps.length && this.theme.heatmaps.length) {
			for (this.j = 0; this.j < this.theme.heatmaps.length; this.j++) {
				this.heatmaps[this.j] = [];
				for (this.i = 0; this.i <= this.heatDepth; this.i++) {
					this.heatmaps[this.j][this.i] = this.hslToHex(0.33 / this.heatDepth * this.i, this.theme.heatmaps[this.j].saturation, this.theme.heatmaps[this.j].lightness);
				}
			}
			this.theme.compiledHeatmaps = this.heatmaps;
		}
	}

	/**
	 * Creates and attaches the meter element.
	 *
	 * @return {Void}
	 */
	createMeter() {
		// Remove old meter if present
		if (this.el.container) {
			this.removeMeter();
		}

		// Set theme
		this.setTheme();

		// Create elements
		this.el.container = this.applyTheme(this.newEl('div'), this.theme.container);
		this.el.count = this.el.container.appendChild(this.applyTheme(this.newEl('div'), this.theme.count));
		this.el.legend = this.el.container.appendChild(this.applyTheme(this.newEl('div'), this.theme.legend));
		this.el.graph = this.o.graph ? this.el.container.appendChild(this.applyTheme(this.newEl('div'), this.theme.graph)) : 0;

		// Add elements to heating array
		this.heating.length = 0;
		for (var key in this.el) {
			if (this.el[key] && this.theme[key].heatOn) {
				this.heating.push({
					name: key,
					el: this.el[key]
				});
			}
		}

		// Graph
		this.cols.length = 0;
		if (this.el.graph) {
			// Create graph
			this.el.graph.style.width = (this.o.history * this.theme.column.width + (this.o.history - 1) * this.theme.column.spacing) + 'px';

			// Add columns
			for (this.i = 0; this.i < this.o.history; this.i++) {
				this.cols[this.i] = this.el.graph.appendChild(this.applyTheme(this.newEl('div'), this.theme.column));
				this.cols[this.i].style.position = 'absolute';
				this.cols[this.i].style.bottom = 0;
				this.cols[this.i].style.right = (this.i * this.theme.column.width + this.i * this.theme.column.spacing) + 'px';
				this.cols[this.i].style.width = this.theme.column.width + 'px';
				this.cols[this.i].style.height = '0px';
			}
		}

		// Set the initial state
		this.positionMeter();
		this.updateCounter();

		// Append container to anchor
		this.anchor.appendChild(this.el.container);

		// Retrieve graph height after it was appended to DOM
		if (this.el.graph) {
			this.graphHeight = this.el.graph.clientHeight;
		}

		// Add event listeners
		if (this.o.toggleOn) {
			if (this.o.toggleOn === 'click') {
				this.el.container.style.cursor = 'pointer';
			}
			this.listener(this.el.container, this.o.toggleOn, this.eventHandler.bind(this));
		}
	}

	/**
	 * Positions the meter based on options.
	 *
	 * @return {Void}
	 */
	positionMeter() {
		this.applyTheme(this.el.container, this.o);
	}

	/**
 * Create a new element.
 *
 * @param  {String} name Element type name.
 *
 * @return {Element}
 */
	newEl(name) {
		return document.createElement(name);
	}

	/**
	 * Apply theme CSS properties to element.
	 *
	 * @param  {Element} element DOM element.
	 * @param  {Object}  theme   Theme object.
	 *
	 * @return {Element}
	 */
	applyTheme(element, theme) {
		for (var name in theme) {
			try {
				element.style[name] = theme[name];
			} catch (e) { }
		}
		return element;
	}

	/**
	 * Return type of the value.
	 *
	 * @param  {Mixed} value
	 *
	 * @return {String}
	 */
	type(value) {
		if (value == null) {
			return String(value);
		}

		if (typeof value === 'object' || typeof value === 'function') {
			return Object.prototype.toString.call(value).match(/\s([a-z]+)/i)[1].toLowerCase() || 'object';
		}

		return typeof value;
	}

	/**
	 * Check whether the value is in an array.
	 *
	 * @param  {Mixed} value
	 * @param  {Array} array
	 *
	 * @return {Integer} Array index or -1 when not found.
	 */
	inArray(value, array) {
		if (this.type(array) !== 'array') {
			return -1;
		}
		if (array.indexOf) {
			return array.indexOf(value);
		}
		for (var i = 0, l = array.length; i < l; i++) {
			if (array[i] === value) {
				return i;
			}
		}
		return -1;
	}

	/**
	 * Poor man's deep object extend.
	 *
	 * Example:
	 *   extend({}, defaults, options);
	 *
	 * @return {Void}
	 */
	extend() {
		var args = arguments;
		for (var key in args[1]) {
			if (args[1].hasOwnProperty(key)) {
				switch (this.type(args[1][key])) {
					case 'object':
						args[0][key] = this.extend({}, args[0][key], args[1][key]);
						break;

					case 'array':
						args[0][key] = args[1][key].slice(0);
						break;

					default:
						args[0][key] = args[1][key];
				}
			}
		}
		return args.length > 2 ?
			//this.extend([args[0]].concat(Array.prototype.slice.call(args, 2)))
			this.extend(args[0], args[2]) :
			args[0];
	}

	/**
	 * Convert HSL color to HEX string.
	 *
	 * @param  {Array} hsl Array with [hue, saturation, lightness].
	 *
	 * @return {Array} Array with [red, green, blue].
	 */
	hslToHex(h, s, l) {
		var r, g, b;
		var v, min, sv, sextant, fract, vsf;

		if (l <= 0.5) {
			v = l * (1 + s);
		} else {
			v = l + s - l * s;
		}

		if (v === 0) {
			return '#000';
		} else {
			min = 2 * l - v;
			sv = (v - min) / v;
			h = 6 * h;
			sextant = Math.floor(h);
			fract = h - sextant;
			vsf = v * sv * fract;
			if (sextant === 0 || sextant === 6) {
				r = v;
				g = min + vsf;
				b = min;
			} else if (sextant === 1) {
				r = v - vsf;
				g = v;
				b = min;
			} else if (sextant === 2) {
				r = min;
				g = v;
				b = min + vsf;
			} else if (sextant === 3) {
				r = min;
				g = v - vsf;
				b = v;
			} else if (sextant === 4) {
				r = min + vsf;
				g = min;
				b = v;
			} else {
				r = v;
				g = min;
				b = v - vsf;
			}
			return '#' + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
		}
	}

	/**
	 * Helper function for hslToHex.
	 */
	componentToHex(c) {
		c = Math.round(c * 255).toString(16);
		return c.length === 1 ? '0' + c : c;
	}

	/**
	 * Manage element event listeners.
	 *
	 * @param  {Node}     element
	 * @param  {Event}    eventName
	 * @param  {Function} handler
	 * @param  {Bool}     remove
	 *
	 * @return {Void}
	 */
	listener(element, eventName, handler, remove) {
		if (element.addEventListener) {
			element[remove ? 'removeEventListener' : 'addEventListener'](eventName, handler, false);
		} else if (element.attachEvent) {
			element[remove ? 'detachEvent' : 'attachEvent']('on' + eventName, handler);
		}
	}

	// Preferred timing funtion
	getTime() {
		var perf = window.performance;
		if (perf && (perf.now || perf.webkitNow)) {
			var perfNow = perf.now ? 'now' : 'webkitNow';
			this.getTime = perf[perfNow].bind(perf);
		} else {
			this.getTime = function () {
				return +new Date();
			};
		}
	};

	makeThemes() {

		// Themes object
		this.themes = {};

		let base = this.themes.base = {
			heatmaps: [],
			container: {
				// Settings
				heatOn: null,
				heatmap: null,
	
				// Styles
				padding: '5px',
				minWidth: '95px',
				height: '30px',
				lineHeight: '30px',
				textAlign: 'right',
				textShadow: 'none'
			},
			count: {
				// Settings
				heatOn: null,
				heatmap: null,
	
				// Styles
				position: 'absolute',
				top: 0,
				right: 0,
				padding: '5px 10px',
				height: '30px',
				fontSize: '24px',
				fontFamily: 'Consolas, Andale Mono, monospace',
				zIndex: 2
			},
			legend: {
				// Settings
				heatOn: null,
				heatmap: null,
	
				// Styles
				position: 'absolute',
				top: 0,
				left: 0,
				padding: '5px 10px',
				height: '30px',
				fontSize: '12px',
				lineHeight: '32px',
				fontFamily: 'sans-serif',
				textAlign: 'left',
				zIndex: 2
			},
			graph: {
				// Settings
				heatOn: null,
				heatmap: null,
	
				// Styles
				position: 'relative',
				boxSizing: 'padding-box',
				MozBoxSizing: 'padding-box',
				height: '100%',
				zIndex: 1
			},
			column: {
				// Settings
				width: 4,
				spacing: 1,
				heatOn: null,
				heatmap: null
			}
		};		
		
		this.themes.dark = this.extend({}, base, {
			heatmaps: [{
				saturation: 0.8,
				lightness: 0.8
			}],
			container: {
				background: '#222',
				color: '#fff',
				border: '1px solid #1a1a1a',
				textShadow: '1px 1px 0 #222'
			},
			count: {
				heatOn: 'color'
			},
			column: {
				background: '#3f3f3f'
			}
		});
		
		// Light theme
		this.themes.light = this.extend({}, base, {
			heatmaps: [{
				saturation: 0.5,
				lightness: 0.5
			}],
			container: {
				color: '#666',
				background: '#fff',
				textShadow: '1px 1px 0 rgba(255,255,255,.5), -1px -1px 0 rgba(255,255,255,.5)',
				boxShadow: '0 0 0 1px rgba(0,0,0,.1)'
			},
			count: {
				heatOn: 'color'
			},
			column: {
				background: '#eaeaea'
			}
		});	
		
		// Colorful theme
		this.themes.colorful = this.extend({}, base, {
			heatmaps: [{
				saturation: 0.5,
				lightness: 0.6
			}],
			container: {
				heatOn: 'backgroundColor',
				background: '#888',
				color: '#fff',
				textShadow: '1px 1px 0 rgba(0,0,0,.2)',
				boxShadow: '0 0 0 1px rgba(0,0,0,.1)'
			},
			column: {
				background: '#777',
				backgroundColor: 'rgba(0,0,0,.2)'
			}
		});

		// Transparent theme
		this.themes.transparent = this.extend({}, base, {
			heatmaps: [{
				saturation: 0.8,
				lightness: 0.5
			}],
			container: {
				padding: 0,
				color: '#fff',
				textShadow: '1px 1px 0 rgba(0,0,0,.5)'
			},
			count: {
				padding: '0 5px',
				height: '40px',
				lineHeight: '40px'
			},
			legend: {
				padding: '0 5px',
				height: '40px',
				lineHeight: '42px'
			},
			graph: {
				height: '40px'
			},
			column: {
				width: 5,
				background: '#999',
				heatOn: 'backgroundColor',
				opacity: 0.5
			}
		});
	};
}

// Default options
FPSMeter.defaults = {
	interval: 100,     // Update interval in milliseconds.
	smoothing: 10,      // Spike smoothing strength. 1 means no smoothing.
	show: 'fps',   // Whether to show 'fps', or 'ms' = frame duration in milliseconds.
	toggleOn: 'click', // Toggle between show 'fps' and 'ms' on this event.
	decimals: 1,       // Number of decimals in FPS number. 1 = 59.9, 2 = 59.94, ...
	maxFps: 60,      // Max expected FPS value.
	threshold: 100,     // Minimal tick reporting interval in milliseconds.

	// Meter position
	position: 'absolute', // Meter position.
	zIndex: 10,         // Meter Z index.
	left: '5px',      // Meter left offset.
	top: '5px',      // Meter top offset.
	right: 'auto',     // Meter right offset.
	bottom: 'auto',     // Meter bottom offset.
	margin: '0 0 0 0',  // Meter margin. Helps with centering the counter when left: 50%;

	// Theme
	theme: 'dark', // Meter theme. Build in: 'dark', 'light', 'transparent', 'colorful'.
	heat: 0,      // Allow themes to use coloring by FPS heat. 0 FPS = red, maxFps = green.

	// Graph
	graph: 0, // Whether to show history graph.
	history: 20 // How many history states to show in a graph.
};

// Expose the FPSMeter class
window.FPSMeter = FPSMeter;

// // UMD (Universal Module Definition)
// (function (root) {

//     if (typeof define === 'function' && define.amd) {

//         // AMD
//         define([], function () {
//             return FPSMeterLibrary;
//         });

//     } else if (typeof module !== 'undefined' && typeof exports === 'object') {

//         // Node.js
//         module.exports = FPSMeterLibrary;

//     } else if (root !== undefined) {

//         // Global variable
//         root.FPSMeterLibrary = FPSMeterLibrary;

//     }

// })(this);